/**
 * SSML Readme 「よく使われる事例」のタグ定義 (正源)。
 *
 * ## なぜタグを別ファイルに置くか
 *
 * 利用者は「どのタグを使うか」ではなく **「こんな読み方にしたい」** から探すため、
 * 事例側の入口はタグによる絞り込みで作る。タグの表記が事例ごとにブレると
 * 絞り込みが機能しなくなるので、**タグは本ファイルの定義だけを正源**とし、
 * 事例データ (`useCases.ts`) は `SsmlUseCaseTag` のキーだけを持つ。
 *
 * ## 将来の拡張 (文字列検索 / YAML 化)
 *
 * - 文字列検索は今回のスコープ外。ただし絞り込みは
 *   `useCaseFilter.ts` に純関数として切り出してあり、検索条件を
 *   `{ tags, keyword }` の形に増やすだけで済むようにしている。
 * - YAML 化する場合、本ファイルは `tags:` セクションへそのまま移送できる
 *   (キー / ラベル / 説明のフラットな構造しか持たせていない)。
 */

/** 事例に付けられるタグのキー (並び順はタグ絞り込みの表示順) */
export type SsmlUseCaseTag =
  | 'station-name'
  | 'line-name'
  | 'direction'
  | 'accent'
  | 'number'
  | 'date'
  | 'apology'
  | 'elderly'
  | 'alphabet'
  | 'noisy'
  | 'phrasing';

/** タグ 1 件の表示情報 */
export interface SsmlUseCaseTagDefinition {
  /** タグキー (事例データが参照する識別子) */
  tag: SsmlUseCaseTag;
  /** 画面に出すラベル (例: 「駅名」) */
  label: string;
  /** タグの意味 (どんな事例に付くか)。tooltip / 将来の説明表示に使う */
  description: string;
}

/**
 * タグ定義の一覧 (配列順が絞り込み UI の表示順)。
 *
 * ⚠️ タグを増やすときは `SsmlUseCaseTag` と本配列の両方に追加する
 *    (テストで「型のキーと配列が一致すること」を固定している)。
 */
export const SSML_USE_CASE_TAGS: readonly SsmlUseCaseTagDefinition[] = [
  {
    tag: 'station-name',
    label: '駅名',
    description: '駅名の聞き取りやすさ・読み・アクセントに関わる事例',
  },
  {
    tag: 'line-name',
    label: '線区名',
    description: '路線名 / 線区名の読みとイントネーションに関わる事例',
  },
  { tag: 'direction', label: '方面', description: '「◯◯方面」の言い回し・アクセントに関わる事例' },
  {
    tag: 'accent',
    label: 'アクセント',
    description: '語のアクセント (どの音を高く読むか) を扱う事例',
  },
  { tag: 'number', label: '数字', description: '時分・番線・本数などの数値表現を扱う事例' },
  { tag: 'date', label: '日付', description: '日付・曜日の読み方を扱う事例' },
  {
    tag: 'apology',
    label: 'お詫び',
    description: '遅延・運転見合わせなどお詫びの雰囲気づくりに関わる事例',
  },
  {
    tag: 'elderly',
    label: '高齢者向け',
    description: '高齢の利用者にも聞き取りやすくすることを狙う事例',
  },
  { tag: 'alphabet', label: '英字', description: '英字・略称・アルファベットの読ませ方を扱う事例' },
  { tag: 'noisy', label: '騒音対策', description: '走行音のある車内でも埋もれないようにする事例' },
  {
    tag: 'phrasing',
    label: '言い回し',
    description: '定型句や語順など文面そのものの調整に関わる事例',
  },
];

/** タグキーからラベルを引く (未定義キーはキーをそのまま返し、描画を落とさない) */
export function ssmlUseCaseTagLabel(tag: string): string {
  return SSML_USE_CASE_TAGS.find((t) => t.tag === tag)?.label ?? tag;
}
